import React, { useEffect, useState } from "react";
import roles from "roles";
import AccessDenied from "components/shared-components/AccessDenied";
import Loading from "components/shared-components/Loading";
import { getUserPermissions } from "../../../utils/permissionUtils";
import { useAuth } from "../../../data/hooks/firebase/useAuth";

const ProtectedRoute = ({ children, routeKey }) => {
  const { role, uid: userId, email } = useAuth();

  const [userPermissions, setUserPermissions] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!role || !userId) {
        setPermissionsLoaded(false);
        return;
      }

      // SuperAdmin has access to everything, no need to load permissions
      if (role === roles.superAdmin) {
        setPermissionsLoaded(true);
        return;
      }

      try {
        const permissions = await getUserPermissions(userId, role);
        setUserPermissions(permissions);
        setPermissionsLoaded(true);
      } catch (error) {
        console.error("Error loading user permissions:", error);
        setPermissionsLoaded(true);
      }
    };

    loadUserPermissions();
  }, [role, userId]);

  const hasAccess = (routeKey) => {
    // If role or permissions not loaded yet, return null to show loading
    if (!role || !permissionsLoaded) return null;

    // Special case: reconciliation routes - only iwan@digitalkiss.nl and samtestt@digitalkiss.nl
    if (routeKey === "shop-products-reconciliation") {
      return email === "iwan@digitalkiss.nl" || email === "samtestt@digitalkiss.nl";
    }

    // SuperAdmin has access to everything
    if (role === roles.superAdmin) return true;

    // For admin users, check user-specific permissions
    if (role === roles.admin) {
      // First check direct permission
      if (userPermissions && userPermissions[routeKey]?.view === true) {
        return true;
      }

      // Special case for reports: if no direct permission found, check parent "shop-reports"
      if (routeKey.startsWith("shop-reports-")) {
        return userPermissions && userPermissions["shop-reports"]?.view === true;
      }

      return false;
    }

    // For regular users, deny access to admin routes
    return false;
  };

  const access = hasAccess(routeKey);

  // Show loading screen when role or permissions are not loaded yet
  if (access === null) {
    return <Loading cover="content" />;
  }

  // Show component if access is granted
  if (access === true) {
    return children;
  }

  // Show access denied if access is false
  return <AccessDenied />;
};

export default ProtectedRoute;
